import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';
import UnifiedPageHeader from '../components/UnifiedPageHeader';

const Container = styled.div`
  background: ${Colors.background};
  min-height: 100vh;
  padding: 0;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${Spacing.xl}px;
`;

const SectionTitle = styled.h2`
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.lg}px;
`;

const SectionSubtitle = styled.p`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.xl}px;
`;

// Onboarding Journey Section
const OnboardingSection = styled.section`
  margin-bottom: ${Spacing.xxl * 2}px;
`;

const OnboardingTimeline = styled.div`
  position: relative;
  padding-left: ${Spacing.xl * 2}px;
  
  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${Colors.border};
  }
`;

const TimelineItem = styled.div<{ completed?: boolean; current?: boolean }>`
  position: relative;
  padding-bottom: ${Spacing.xl}px;
  
  &::before {
    content: '';
    position: absolute;
    left: -${Spacing.xl * 2 - 12}px;
    top: 8px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => 
      props.completed ? Colors.success :
      props.current ? Colors.primary :
      Colors.border
    };
    border: 3px solid ${Colors.surface};
    box-shadow: 0 0 0 1px ${Colors.border};
    z-index: 1;
  }
  
  &:last-child {
    padding-bottom: 0;
  }
`;

const TimelineCard = styled.div<{ current?: boolean }>`
  background: ${Colors.surface};
  border: 1px solid ${props => props.current ? Colors.primary : Colors.border};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${props => props.current ? Shadows.md : Shadows.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: ${Shadows.md};
    transform: translateX(4px);
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.md}px;
`;

const TimelineTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
`;

const TimelineBadge = styled.span<{ type: 'completed' | 'current' | 'upcoming' }>`
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  
  ${props => {
    switch(props.type) {
      case 'completed':
        return `
          background: ${Colors.success}20;
          color: ${Colors.success};
        `;
      case 'current':
        return `
          background: ${Colors.primary}20;
          color: ${Colors.primary};
        `;
      case 'upcoming':
        return `
          background: ${Colors.borderLight};
          color: ${Colors.textTertiary};
        `;
    }
  }}
`;

const TimelineContent = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.md}px;
  line-height: 1.6;
`;

const TimelineActions = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
`;

const ActionButton = styled.button`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${Colors.primary};
  color: white;
  border: none;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.primaryDark};
  }
  
  &.secondary {
    background: ${Colors.surfaceHover};
    color: ${Colors.textPrimary};
    
    &:hover {
      background: ${Colors.borderLight};
    }
  }
`;

// Resource Categories
const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xxl * 2}px;
`;

const ResourceCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${Shadows.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: ${Shadows.md};
    border-color: ${Colors.primary};
    transform: translateY(-2px);
  }
`;

const ResourceIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${BorderRadius.md}px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.xl}px;
  margin-bottom: ${Spacing.md}px;
`;

const ResourceTitle = styled.h3`
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.sm}px;
`;

const ResourceDescription = styled.p`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  line-height: 1.5;
  margin-bottom: ${Spacing.md}px;
`;

const ResourceCount = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
`;

// Help & Support Section
const SupportSection = styled.section`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xl}px;
  box-shadow: ${Shadows.sm};
`;

const SupportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${Spacing.lg}px;
`;

const SupportItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  padding: ${Spacing.md}px;
  background: ${Colors.background};
  border-radius: ${BorderRadius.md}px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
  }
`;

const SupportIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${BorderRadius.sm}px;
  background: ${Colors.primary}10;
  color: ${Colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.lg}px;
`;

const SupportInfo = styled.div`
  flex: 1;
`;

const SupportTitle = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const SupportDetail = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
`;

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'upcoming';
  dueDate?: string;
  actions: Array<{ label: string; primary?: boolean }>;
}

interface ResourceCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  itemCount: number;
}

interface SupportChannel {
  id: string;
  title: string;
  detail: string;
  icon: string;
}

const BelongingResourcesScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [onboardingSteps] = useState<OnboardingStep[]>([
    {
      id: '1',
      title: 'Welcome & Company Overview',
      description: 'Get to know Clouds on Mars, our mission, values, and culture. Complete your profile and connect with your team.',
      status: 'completed',
      actions: [
        { label: 'Review Materials', primary: false }
      ]
    },
    {
      id: '2',
      title: 'Meet Your Buddy & Team',
      description: 'Connect with your onboarding buddy Sarah Chen and schedule 1:1s with team members. Join your first team standup.',
      status: 'current',
      dueDate: 'Due in 2 days',
      actions: [
        { label: 'Schedule Meeting', primary: true },
        { label: 'View Team', primary: false }
      ]
    },
    {
      id: '3',
      title: 'Complete Mandatory Training',
      description: 'Finish required training modules including Security Awareness, Data Privacy, and our Code of Conduct.',
      status: 'upcoming',
      dueDate: 'Due in 5 days',
      actions: [
        { label: 'Start Training', primary: true }
      ]
    },
    {
      id: '4',
      title: 'First Week Check-in',
      description: 'Meet with your manager to discuss goals, expectations, and create your 30-60-90 day plan.',
      status: 'upcoming',
      dueDate: 'Due in 7 days',
      actions: [
        { label: 'Prepare Questions', primary: false }
      ]
    },
    {
      id: '5',
      title: 'Join Communities & Events',
      description: 'Explore employee resource groups, sign up for upcoming events, and connect with colleagues who share your interests.',
      status: 'upcoming',
      dueDate: 'Week 2',
      actions: [
        { label: 'Browse Communities', primary: true }
      ]
    }
  ]);

  const [resourceCategories] = useState<ResourceCategory[]>([
    {
      id: '1',
      title: 'Company Handbook',
      description: 'Policies, procedures, benefits, and everything you need to know about working here.',
      icon: '',
      color: Colors.primary,
      itemCount: 24
    },
    {
      id: '2',
      title: 'Learning & Development',
      description: 'Training courses, skill development programs, and career growth resources.',
      icon: '',
      color: Colors.success,
      itemCount: 156
    },
    {
      id: '3',
      title: 'Well-being Resources',
      description: 'Mental health support, fitness programs, and work-life balance guidance.',
      icon: '',
      color: Colors.accent,
      itemCount: 42
    },
    {
      id: '4',
      title: 'Tools & Systems',
      description: 'Guides for all the tools and systems you use daily.',
      icon: '',
      color: Colors.warning,
      itemCount: 38
    },
    {
      id: '5',
      title: 'DEI Resources',
      description: 'Diversity, equity, and inclusion initiatives, training, and support.',
      icon: '',
      color: Colors.info,
      itemCount: 31
    },
    {
      id: '6',
      title: 'Manager Toolkit',
      description: 'Resources for people managers to lead effectively and support their teams.',
      icon: '',
      color: Colors.secondary,
      itemCount: 27
    }
  ]);

  const [supportChannels] = useState<SupportChannel[]>([
    {
      id: '1',
      title: 'HR Support',
      detail: 'hr@cloudsonmars.com',
      icon: ''
    },
    {
      id: '2',
      title: 'IT Help Desk',
      detail: 'Extension 1234',
      icon: ''
    },
    {
      id: '3',
      title: 'Employee Assistance',
      detail: '24/7 Confidential',
      icon: ''
    },
    {
      id: '4',
      title: 'Onboarding Buddy',
      detail: 'Sarah Chen',
      icon: ''
    }
  ]);

  const handleStepAction = (stepId: string, action: string) => {
    console.log(`Performing ${action} for step ${stepId}`);
  };

  const handleResourceClick = (categoryId: string) => {
    console.log(`Opening resource category ${categoryId}`);
  };

  const handleSupportClick = (channelId: string) => {
    console.log(`Contacting support channel ${channelId}`);
  };

  return (
    <Container>
      <UnifiedPageHeader
        title={t('belonging.resources.title', 'Resources & Support')}
        subtitle={t('belonging.resources.subtitle', 'Everything you need to succeed and feel at home')}
        metadata={{
          timestamp: new Date()
        }}
        actions={[
          {
            label: t('belonging.resources.askQuestion', 'Ask a Question'),
            onClick: () => console.log('Ask question'),
            variant: 'primary',
            icon: ''
          }
        ]}
      />

      <ContentWrapper>
        {/* Onboarding Journey - Show for new employees */}
        <OnboardingSection>
          <SectionTitle>{t('belonging.onboarding.title', 'Your Onboarding Journey')}</SectionTitle>
          <SectionSubtitle>
            {t('belonging.onboarding.subtitle', 'Track your progress and complete important milestones')}
          </SectionSubtitle>
          
          <OnboardingTimeline>
            {onboardingSteps.map(step => (
              <TimelineItem
                key={step.id}
                completed={step.status === 'completed'}
                current={step.status === 'current'}
              >
                <TimelineCard current={step.status === 'current'}>
                  <TimelineHeader>
                    <TimelineTitle>{step.title}</TimelineTitle>
                    <TimelineBadge type={step.status}>
                      {step.status === 'completed' ? '✓ Completed' :
                       step.status === 'current' ? '⏳ In Progress' :
                       '📅 Upcoming'}
                    </TimelineBadge>
                  </TimelineHeader>
                  <TimelineContent>{step.description}</TimelineContent>
                  {step.dueDate && (
                    <TimelineContent style={{ fontWeight: 600, color: Colors.textPrimary }}>
                      {step.dueDate}
                    </TimelineContent>
                  )}
                  <TimelineActions>
                    {step.actions.map((action, idx) => (
                      <ActionButton
                        key={idx}
                        className={action.primary ? '' : 'secondary'}
                        onClick={() => handleStepAction(step.id, action.label)}
                      >
                        {action.label}
                      </ActionButton>
                    ))}
                  </TimelineActions>
                </TimelineCard>
              </TimelineItem>
            ))}
          </OnboardingTimeline>
        </OnboardingSection>

        {/* Resource Categories */}
        <section style={{ marginBottom: `${Spacing.xxl * 2}px` }}>
          <SectionTitle>{t('belonging.resources.categories', 'Resource Library')}</SectionTitle>
          <SectionSubtitle>
            {t('belonging.resources.categoriesSubtitle', 'Find guides, policies, and tools to help you thrive')}
          </SectionSubtitle>
          
          <ResourceGrid>
            {resourceCategories.map(category => (
              <ResourceCard
                key={category.id}
                onClick={() => handleResourceClick(category.id)}
              >
                <ResourceIcon color={category.color}>
                  {category.icon}
                </ResourceIcon>
                <ResourceTitle>{category.title}</ResourceTitle>
                <ResourceDescription>{category.description}</ResourceDescription>
                <ResourceCount>{category.itemCount} resources</ResourceCount>
              </ResourceCard>
            ))}
          </ResourceGrid>
        </section>

        {/* Help & Support */}
        <SupportSection>
          <SectionTitle>{t('belonging.support.title', 'Need Help?')}</SectionTitle>
          <SectionSubtitle>
            {t('belonging.support.subtitle', 'Multiple ways to get the support you need')}
          </SectionSubtitle>
          
          <SupportGrid>
            {supportChannels.map(channel => (
              <SupportItem
                key={channel.id}
                onClick={() => handleSupportClick(channel.id)}
              >
                <SupportIcon>{channel.icon}</SupportIcon>
                <SupportInfo>
                  <SupportTitle>{channel.title}</SupportTitle>
                  <SupportDetail>{channel.detail}</SupportDetail>
                </SupportInfo>
              </SupportItem>
            ))}
          </SupportGrid>
        </SupportSection>
      </ContentWrapper>
    </Container>
  );
};

export default BelongingResourcesScreen;